// File: src/components/layout/Footer.tsx

import React from 'react';
import { Box, Container, Typography, Link, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import * as CookieConsent from 'vanilla-cookieconsent';
import { LanguageSwitcher } from '../LanguageSwitcher';

const Footer = () => {
    const theme = useTheme();
    const { t } = useTranslation();

    const FooterHeader = ({ children }: { children: React.ReactNode }) => (
        <Typography
            variant="subtitle2"
            color="text.primary"
            sx={{ fontWeight: 700, mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}
        >
            {children}
        </Typography>
    );

    const FooterLink = ({
                            to,
                            href,
                            onClick,
                            children,
                        }: {
        to?: string;
        href?: string;
        onClick?: () => void;
        children: React.ReactNode;
    }) => {
        const commonSx = {
            display: 'block',
            mb: 1,
            textAlign: 'left' as const,
            '&:hover': { color: 'primary.main' },
            transition: 'color 0.2s',
        };

        if (onClick) {
            return (
                <Link
                    component="button"
                    onClick={onClick}
                    color="text.secondary"
                    underline="none"
                    variant="body2"
                    sx={commonSx}
                >
                    {children}
                </Link>
            );
        }

        const props = to ? { component: RouterLink, to } : { href };
        return (
            <Link
                {...props}
                color="text.secondary"
                underline="none"
                variant="body2"
                sx={commonSx}
            >
                {children}
            </Link>
        );
    };

    const handleCookieSettings = () => {
        CookieConsent.showPreferences();
    };

    return (
        <Box
            component="footer"
            sx={{
                py: 6,
                mt: 'auto',
                backgroundColor: theme.palette.background.paper,
                borderTop: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={4}>
                    {/* Column 1: Brand & Info */}
                    <Grid item xs={12} md={4}>
                        <Typography variant="h6" color="text.primary" sx={{ fontWeight: 700, mb: 1 }}>
                            {t('app_name')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 300 }}>
                            {t('footer.description')}
                        </Typography>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 2, opacity: 0.7 }}
                        >
                            ISN Group
                        </Typography>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ mt: 1, opacity: 0.7 }}
                        >
                            © {new Date().getFullYear()} All rights reserved.
                        </Typography>
                    </Grid>

                    {/* Column 2: Company */}
                    <Grid item xs={6} md={3}>
                        <FooterHeader>Company</FooterHeader>
                        <FooterLink to="/">{t('footer.home')}</FooterLink>
                        <FooterLink href="#">{t('footer.about')}</FooterLink>
                        <FooterLink to="/pricing">{t('footer.pricing')}</FooterLink>
                        <FooterLink href="mailto:support@ecomai.time2future.com">
                            {t('footer.contact')}
                        </FooterLink>
                    </Grid>

                    {/* Column 3: Legal */}
                    <Grid item xs={6} md={3}>
                        <FooterHeader>Legal</FooterHeader>
                        <FooterLink to="/terms">{t('footer.terms')}</FooterLink>
                        <FooterLink to="/privacy">{t('footer.privacy')}</FooterLink>
                        <FooterLink to="/refund-policy">
                            {t('footer.refund_policy') || 'Refund Policy'}
                        </FooterLink>
                        <FooterLink to="/imprint">{t('footer.imprint')}</FooterLink>
                    </Grid>

                    {/* Column 4: Language + Cookie */}
                    <Grid
                        item
                        xs={12}
                        md={2}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-end',
                            alignItems: { xs: 'flex-start', md: 'flex-end' },
                        }}
                    >
                        <Box sx={{ mb: 1 }}>
                            <LanguageSwitcher variant="sidebar" />
                        </Box>

                        <Link
                            component="button"
                            onClick={handleCookieSettings}
                            color="text.secondary"
                            underline="hover"
                            variant="caption"
                            sx={{
                                opacity: 0.7,
                                textAlign: 'right',
                                mt: { xs: 1, md: 0 },
                            }}
                        >
                            Cookie preferences
                        </Link>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default Footer;
